import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"
import styled from "styled-components"

import _debounce from "lodash/debounce"
import _maxBy from "lodash/maxBy"
import _minBy from "lodash/minBy"

import Spinner from "../Spinner"

import GraphRenderer from "./Digraph.renderer"
import { runLayout } from "./Digraph.layout"

const MAX_NODES = 50

const Wrap = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  position: relative;
`

// const Watermark = styled.div`
//   position: absolute;
//   right: 140px;
//   bottom: 0.5rem;
//   color: white;
//   opacity: 0.2;
//   font-size: 0.6rem;
// `

type TDigraphEventData = {
  type: "node" | "link"
  data: any
  source?: string
  target?: string
  id?: string
}

type TSimulation = {
  tick: any
  nodes: any
}

type TBounds = {
  x: [number, number]
  y: [number, number]
}

interface IDigraphProps {
  /**
   * Multiplier for the radius of the dotted circle around active (selected) nodes. Default 1 = 40px.
   * @default 1
   */
  activeNodeBorderScale: number
  /**
   * graph data - ngraph.graph format.
   * See https://github.com/anvaka/ngraph.graph for programmatic construction
   * or https://github.com/anvaka/ngraph.fromdot for converting dot format graphs to this structure
   */
  graph: any
  /**
   * key within node data which indicates graph depth. Actual value within this property should be a number.
   * @default ""
   */
  depthKey: string
  /**
   * Component height
   * @default 600
   */
  height: number
  /**
   * Theme palette key, to be used for default node and edge coloring. e.g. primary/secondary/warning/error
   * @default "primary"
   */
  paletteKey: string
  /**
   * Event handler for when a node or edge is clicked
   */
  onElementSelected?: (data: any) => void
  /**
   * Event handler for when a node or edge is hovered
   */
  onElementHovered?: (data: any) => void
  /**
   * Component width
   * @default 800
   */
  width: number
}

const getProp = (data: any, key: string) =>
  Object.keys(data).includes(key) ? data[key] : null

export default function Digraph({
  activeNodeBorderScale = 1,
  graph,
  depthKey = "",
  paletteKey = "primary",
  onElementSelected,
  onElementHovered,
  width = 800,
  height = 600
}: IDigraphProps) {
  const simulation = useRef<TSimulation | null>(null)
  const [finalNodes, setFinalNodes] = useState([])
  const [bounds, setBounds] = useState<TBounds | null>(null)

  const _initSimulation = useCallback(() => {
    if (
      simulation.current &&
      simulation.current.tick &&
      typeof simulation.current.tick === "function"
    ) {
      simulation.current.tick(300).restart()
    }
    calculateBounds()
  }, [])

  const _calculateFinalLayout = useCallback(() => {
    if (!simulation.current) {
      return
    }
    setFinalNodes(simulation.current.nodes())
    calculateBounds()
  }, [])

  // things to go in memo...are things which should not be recomputed each render
  // simulation
  // stats
  // graphData
  const { sim, graphData, stats } = useMemo(
    () =>
      runLayout({
        graph,
        depthKey,
        width,
        height,
        onTick: _debounce(_calculateFinalLayout, 200)
      }),
    [graph, width, height, _calculateFinalLayout, depthKey]
  )

  // ensure this persists
  simulation.current = sim

  // init sim on mount
  useEffect(() => {
    if (simulation && simulation.current) {
      _initSimulation()
    }
  }, [_initSimulation])

  if (!stats.nodesCount) {
    return <p>No nodes specified</p>
  }

  if (!stats.linksCount) {
    return <p>No edges specified</p>
  }

  if (stats.nodesCount > MAX_NODES) {
    return <p>{`Nodes > ${MAX_NODES} is not currently supported`}</p>
  }

  function handleDataSelected(data: TDigraphEventData) {
    if (onElementSelected && typeof onElementSelected === "function") {
      onElementSelected(data)
    }
  }
  function handleDataHovered(data: TDigraphEventData) {
    if (onElementHovered && typeof onElementHovered === "function") {
      onElementHovered(data)
    }
  }

  function calculateBounds() {
    if (!simulation.current) {
      return
    }

    const positions = simulation.current
      .nodes()
      .map((n: { x: number; y: number }) => ({ x: n.x, y: n.y }))
    if (!positions || !positions.length) {
      return
    }
    const newBounds: TBounds = {
      x: [
        getProp(_minBy(positions, "x"), "x"),
        getProp(_maxBy(positions, "x"), "x")
      ],
      y: [
        getProp(_minBy(positions, "y"), "y"),
        getProp(_maxBy(positions, "y"), "y")
      ]
    }

    setBounds(newBounds)
  }

  return (
    <Wrap>
      {bounds ? (
        <GraphRenderer
          activeNodeBorderScale={activeNodeBorderScale}
          bounds={bounds}
          nodes={finalNodes}
          links={graphData.links}
          width={width}
          height={height}
          nodeTextFormatter={n => n.id}
          paletteKey={paletteKey}
          onDataSelected={handleDataSelected}
          onDataHovered={handleDataHovered}
        />
      ) : (
        <Spinner />
      )}
    </Wrap>
  )
}
